import { useState, useEffect, useCallback } from "react";
import { Loader, FileText, Shield } from "lucide-react";
import PolicyDetails from "./common/PolicyDetails";
import insurancePolicyService from "../lib/InsurancePolicyService";
import { useAuth } from "../hooks/useAuth";

const InsurancePoliciesView = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, getIdToken } = useAuth();

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!user) {
        setLoading(false);
        return;
      }
      const token = await getIdToken();
      if (!token) return;
      const data = await insurancePolicyService.checkExistingPolicies(token);
      setPolicies(data || []);
    } catch (err) {
      console.error("Failed to fetch policies", err);
      setError(err.message || "Failed to load policies");
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    if (user) fetchPolicies();
  }, [fetchPolicies, user]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <Loader className="w-8 h-8 text-crimsonGlory animate-spin mb-4" />
        <p className="text-gray-600">Loading your policies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={fetchPolicies}
            className="mt-4 text-sm text-red-500 hover:text-red-700 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-crimsonGlory/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-crimsonGlory" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            No Policies Yet
          </h1>
          <p className="text-gray-500">
            Upload your insurance policy during onboarding to see your policy
            details here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-crimsonGlory/10 rounded-full flex items-center justify-center">
          <Shield className="w-5 h-5 text-crimsonGlory" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">Your Policies</h1>
      </div>
      <div className="space-y-4">
        {policies.map((policy, index) => (
          <PolicyDetails key={policy.id || index} checkin={policy} />
        ))}
      </div>
    </div>
  );
};

export default InsurancePoliciesView;
